import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isOrderType } from '../../constants/visitTypes';
import { OrderDetailPanel } from '../order/OrderDetailPanel';
import {
  ArrowLeft,
  AlertTriangle,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  ShieldCheck,
  Pencil,
  StickyNote,
  LockOpen,
  Save,
} from 'lucide-react';

import {
  getVisit,
  updateVisitData,
  signTask,
  approveTask,
  breakTaskSignatures,
  updateVisitNotes,
  getTaskSignatures,
  getVisitPdf,
  client,
} from '../../services/api';
import type {
  VisitResponse,
  TaskSignatureResponse,
} from '../../services/api/types.gen';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { OASISForm, OASISFormData, ReadonlyChartView } from '../oasis';
import { SignatureInvalidationDialog } from '../SignatureInvalidationDialog';
import { AttestationDialog } from '../signing/AttestationDialog';
import { SignatureCard } from '../signing/SignatureCard';
import {
  resolveAttestationFamily,
  selectAttestationForFamily,
} from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import TaskDocumentsSection from '../documents/TaskDocumentsSection';
import { useStore } from '../../store';
import { useToast } from '../../hooks/useToastContext';
import {
  EDITABLE_STATUSES,
  STATUS_DISPLAY_LABELS,
  getStatusBadgeVariant,
  formatDate,
  useTaskPermissions,
} from '../task/TaskDetailPanel';

const formatDateTime = (dateString: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

interface VisitDetailPanelProps {
  visitId: string;
  patientId: string;
  onBack?: () => void;
  onVisitUpdated?: (visit: VisitResponse) => void;
}

export const VisitDetailPanel: React.FC<VisitDetailPanelProps> = ({
  visitId,
  patientId,
  onBack,
  onVisitUpdated,
}) => {
  const navigate = useNavigate();
  const user = useStore(state => state.user);
  const { error: showError } = useToast();
  const {
    attestations,
    isLoading: attestationsLoading,
    error: attestationsError,
  } = useActiveAttestations();

  useEffect(() => {
    if (attestationsError) {
      showError(
        'Could not load signing text',
        'Refresh the page or try again in a moment.'
      );
    }
  }, [attestationsError, showError]);

  // Core visit state
  const [visit, setVisit] = useState<VisitResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Signature state
  const [signatures, setSignatures] = useState<TaskSignatureResponse[]>([]);
  const [signingLoading, setSigningLoading] = useState(false);
  const [approvingLoading, setApprovingLoading] = useState(false);
  const [editingLoading, setEditingLoading] = useState(false);
  const [invalidationLoading, setInvalidationLoading] = useState(false);

  // Signature invalidation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<{
    data: Record<string, unknown>;
    isFormSave: boolean;
  } | null>(null);
  const [formKey, setFormKey] = useState(0);
  const savePromiseRef = useRef<{
    resolve: () => void;
    reject: () => void;
  } | null>(null);
  const [isUnlockAction, setIsUnlockAction] = useState(false);

  // Task notes state
  const [taskNotes, setTaskNotes] = useState('');
  const [taskNotesLoading, setTaskNotesLoading] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [draftNotes, setDraftNotes] = useState('');

  // Transition notes state (gates sign/approve/break actions)
  const [showTransitionNotesModal, setShowTransitionNotesModal] =
    useState(false);
  const [transitionNotes, setTransitionNotes] = useState('');
  const [pendingTransitionAction, setPendingTransitionAction] = useState<
    (() => Promise<void>) | null
  >(null);

  // PDF state
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // --- Derived permission state ---

  const isEditable = visit ? EDITABLE_STATUSES.has(visit.status) : false;
  const isReadonly = !isEditable;

  // Role-based checks: any user with the matching role flag in the visit's
  // clinic can perform the associated actions.
  // NOTE: visit.assigned_clinician_user_id and visit.case_manager_user_id
  // indicate who the visit shows up for in a personal TODO list (not yet
  // implemented). They do NOT gate action permissions — role flags above do.
  const {
    hasClinicianRole,
    hasCaseManagerRole,
    canSign,
    canApprove,
    canBreakSignatures,
  } = useTaskPermissions(user, visit);

  const currentUserHasSigned = useMemo(() => {
    if (!user?.id || !signatures.length) return false;
    return signatures.some(sig => sig.signed_by_id === user.id);
  }, [user?.id, signatures]);

  const signButtonLabel = useMemo(() => {
    if (!visit) return 'Sign & Submit';
    if (visit.status === 'submitted') return 'Sign & Return for Review';
    if (visit.status === 'pending_clinician_signature')
      return 'Sign & Complete';
    return 'Sign & Submit';
  }, [visit?.status]);

  const editButtonLabel = useMemo(() => {
    if (!visit) return 'Unlock & Edit';
    if (
      visit.status === 'submitted_with_signature' &&
      hasClinicianRole &&
      !hasCaseManagerRole
    )
      return 'Unsubmit & Edit';
    return 'Unlock & Edit';
  }, [visit?.status, hasClinicianRole, hasCaseManagerRole]);

  // --- Data fetching ---

  useEffect(() => {
    const fetchData = async () => {
      if (!visitId) return;

      try {
        setLoading(true);
        setError(null);

        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId }, client }),
          getTaskSignatures({
            path: { task_id: visitId },
            client,
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          setTaskNotes(visitData.data.notes ?? '');
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
      } catch (err) {
        console.error('Error fetching visit data:', err);
        setError('Failed to load visit details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [visitId, onVisitUpdated]);

  // --- OASIS form save (with signature invalidation dialog) ---

  const performSave = async (data: Record<string, unknown>) => {
    if (!visit || !visitId) return;

    setSaveError(null);

    const updatedVisit = await updateVisitData({
      path: { visit_id: visitId },
      body: { visit_data: data },
      client,
    });

    if (updatedVisit?.error) {
      const detail = (updatedVisit.error as { detail?: string })?.detail;
      const isReadOnlyError = detail?.toLowerCase().includes('read-only');

      if (isReadOnlyError) {
        showError(
          'Read-Only',
          'This visit is now in a read-only state. The page will reload to reflect the latest changes.'
        );
        setTimeout(() => window.location.reload(), 3000);
      }

      throw new Error('Failed to save visit data');
    }

    if (updatedVisit?.data) {
      setVisit(updatedVisit.data);
      onVisitUpdated?.(updatedVisit.data);
    }

    setSignatures([]);
  };

  const handleOASISFormSave = async (formSaveData: OASISFormData) => {
    if (!visit || !visitId) return;

    if (signatures.length > 0) {
      setPendingSaveData({ data: formSaveData, isFormSave: true });
      setShowConfirmDialog(true);

      return new Promise<void>((resolve, reject) => {
        savePromiseRef.current = { resolve, reject };
      });
    }

    try {
      await performSave(formSaveData);
    } catch (err) {
      console.error('Error saving OASIS form data:', err);
      setSaveError('Failed to save form data. Please try again.');
      throw err;
    }
  };

  const handleConfirmSave = async () => {
    if (!pendingSaveData) return;

    setShowConfirmDialog(false);
    setInvalidationLoading(true);
    try {
      await performSave(pendingSaveData.data);
      savePromiseRef.current?.resolve();
    } catch (err) {
      console.error('Error saving after confirmation:', err);
      savePromiseRef.current?.reject();
    } finally {
      setInvalidationLoading(false);
      setPendingSaveData(null);
      savePromiseRef.current = null;
    }
  };

  const handleCancelSave = () => {
    setShowConfirmDialog(false);

    if (isUnlockAction) {
      setIsUnlockAction(false);
      return;
    }

    savePromiseRef.current?.reject();
    savePromiseRef.current = null;

    if (pendingSaveData?.isFormSave) {
      setFormKey(prev => prev + 1);
    }

    setPendingSaveData(null);
  };

  // --- Unlock from readonly (break signatures via invalidation dialog) ---

  const handleReturnToEditMode = () => {
    if (!visit || signatures.length === 0) return;
    setIsUnlockAction(true);
    setShowConfirmDialog(true);
  };

  const handleUnlockConfirm = async () => {
    if (!visit) return;
    setShowConfirmDialog(false);
    setInvalidationLoading(true);
    try {
      const result = await breakTaskSignatures({
        path: { task_id: visit.id },
        client,
      });
      if (result.error) {
        console.error('Error unlocking visit:', result.error);
        setSaveError('Failed to unlock visit. Please try again.');
        return;
      }
      const [visitData, signaturesData] = await Promise.all([
        getVisit({ path: { visit_id: visit.id }, client }),
        getTaskSignatures({ path: { task_id: visit.id }, client }),
      ]);
      if (visitData?.data) {
        setVisit(visitData.data);
        onVisitUpdated?.(visitData.data);
      }
      if (signaturesData?.data) {
        setSignatures(signaturesData.data);
      }
    } catch (err) {
      console.error('Error unlocking visit:', err);
      setSaveError('Failed to unlock visit. Please try again.');
    } finally {
      setInvalidationLoading(false);
      setIsUnlockAction(false);
    }
  };

  // --- Transition notes pattern (gates sign/approve/break through notes modal) ---

  const executeWithTransitionNotes = (action: () => Promise<void>) => {
    setPendingTransitionAction(() => action);
    setTransitionNotes(taskNotes);
    setShowTransitionNotesModal(true);
  };

  const handleConfirmTransition = async () => {
    if (!pendingTransitionAction || !visitId) return;

    if (transitionNotes !== taskNotes) {
      try {
        await updateVisitNotes({
          path: { visit_id: visitId },
          body: { notes: transitionNotes || null },
          client,
        });
        setTaskNotes(transitionNotes);
      } catch (err) {
        console.error('Error saving notes:', err);
        setSaveError('Failed to save task notes.');
        setShowTransitionNotesModal(false);
        setPendingTransitionAction(null);
        return;
      }
    }

    setShowTransitionNotesModal(false);
    await pendingTransitionAction();
    setPendingTransitionAction(null);
  };

  const handleCancelTransition = () => {
    setShowTransitionNotesModal(false);
    setPendingTransitionAction(null);
  };

  // --- Sign & Submit ---

  const visitAttestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(
      attestations,
      resolveAttestationFamily('visit')
    );
  }, [attestations]);
  const [signAttestationOpen, setSignAttestationOpen] = useState(false);
  const [approveAttestationOpen, setApproveAttestationOpen] = useState(false);

  const doSignAndSubmit = async () => {
    if (!visitId || !visitAttestation) return;

    try {
      setSigningLoading(true);
      setSaveError(null);

      const signResult = await signTask({
        path: { task_id: visitId },
        body: { attestation_version: visitAttestation.version },
        client,
      });

      if (signResult.error) {
        console.error('Error signing visit:', signResult.error);
        setSaveError('Failed to sign and submit. Please try again.');
        return;
      }

      const [visitData, signaturesData] = await Promise.all([
        getVisit({ path: { visit_id: visitId }, client }),
        getTaskSignatures({
          path: { task_id: visitId },
          client,
        }),
      ]);

      if (visitData?.data) {
        setVisit(visitData.data);
        onVisitUpdated?.(visitData.data);
      }
      if (signaturesData?.data) {
        setSignatures(signaturesData.data);
      }
    } catch (err) {
      console.error('Error signing visit:', err);
      setSaveError('Failed to sign and submit. Please try again.');
    } finally {
      setSigningLoading(false);
      setSignAttestationOpen(false);
    }
  };

  const handleSignAndSubmit = () => {
    setSignAttestationOpen(true);
  };

  // --- Sign & Complete (approve) ---

  const doApprove = async () => {
    if (!visitId || !visitAttestation) return;

    try {
      setApprovingLoading(true);
      setSaveError(null);

      const result = await approveTask({
        path: { task_id: visitId },
        body: { attestation_version: visitAttestation.version },
        client,
      });

      if (result.error) {
        console.error('Error completing visit:', result.error);
        setSaveError('Failed to complete visit. Please try again.');
        return;
      }

      const [visitData, signaturesData] = await Promise.all([
        getVisit({ path: { visit_id: visitId }, client }),
        getTaskSignatures({
          path: { task_id: visitId },
          client,
        }),
      ]);

      if (visitData?.data) {
        setVisit(visitData.data);
        onVisitUpdated?.(visitData.data);
      }
      if (signaturesData?.data) {
        setSignatures(signaturesData.data);
      }
    } catch (err) {
      console.error('Error completing visit:', err);
      setSaveError('Failed to complete visit. Please try again.');
    } finally {
      setApprovingLoading(false);
      setApproveAttestationOpen(false);
    }
  };

  const handleApprove = () => {
    // Transition notes are saved in handleConfirmTransition before this
    // callback runs; doApprove only needs the attestation dialog (no notes).
    executeWithTransitionNotes(async () => {
      setApproveAttestationOpen(true);
    });
  };

  // --- Break signatures from header (non-readonly, goes through transition notes) ---

  const handleBreakSignatures = async () => {
    if (!visitId) return;

    const doBreak = async () => {
      try {
        setEditingLoading(true);
        setSaveError(null);

        const result = await breakTaskSignatures({
          path: { task_id: visitId },
          client,
        });

        if (result.error) {
          console.error('Error breaking signatures:', result.error);
          setSaveError('Failed to break signatures. Please try again.');
          return;
        }

        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId }, client }),
          getTaskSignatures({
            path: { task_id: visitId },
            client,
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
      } catch (err) {
        console.error('Error breaking signatures:', err);
        setSaveError('Failed to break signatures. Please try again.');
      } finally {
        setEditingLoading(false);
      }
    };

    executeWithTransitionNotes(doBreak);
  };

  // --- PDF export (opens in new tab) ---

  const handleExportPdf = async () => {
    if (!visitId) return;

    try {
      setPdfLoading(true);
      setPdfError(null);
      const response = await getVisitPdf({
        client,
        path: { visit_id: visitId },
      });

      if (!response.data) {
        setPdfError('No PDF data received from the server.');
        return;
      }

      const blob = new Blob([response.data as BlobPart], {
        type: 'application/pdf',
      });
      if (!blob) {
        setPdfError('Failed to export PDF. Please try again.');
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chart_review_${visitId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setPdfError('Failed to export PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  // --- Render ---

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-muted-foreground'>Loading visit details...</p>
        </div>
      </div>
    );
  }

  if (error || !visit) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Visit</h3>
          <p className='text-muted-foreground mb-4'>
            {error || 'Visit not found'}
          </p>
          <Button
            onClick={() =>
              onBack ? onBack() : navigate(`/patients/${patientId}`)
            }
            variant='outline'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Patient
          </Button>
        </div>
      </div>
    );
  }

  // Delegate to OrderDetailPanel for order visit types
  if (isOrderType(visit.visit_type)) {
    return <OrderDetailPanel orderId={visit.id} onBack={onBack} />;
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Visit Header Bar */}
      <div className='flex items-center justify-between px-5 py-3 border-b bg-muted/20'>
        <div className='flex items-center gap-3 min-w-0'>
          <Button
            variant='outline'
            size='sm'
            onClick={() =>
              onBack ? onBack() : navigate(`/patients/${patientId}`)
            }
            className='flex items-center gap-1.5 flex-shrink-0'
          >
            <ArrowLeft className='h-3.5 w-3.5' />
            See All Visits
          </Button>
          <div className='h-4 w-px bg-border' />
          <div className='min-w-0'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-medium text-foreground'>
                {visit.visit_type_display_name}
              </span>
              <span className='text-sm text-muted-foreground'>
                {formatDate(visit.visit_date)}
              </span>
            </div>
            <div className='text-xs text-muted-foreground'>
              Last updated:{' '}
              {visit.visit_data_last_updated
                ? formatDateTime(visit.visit_data_last_updated)
                : 'Never'}
            </div>
          </div>
        </div>

        <div className='flex items-center gap-2 flex-shrink-0'>
          {/* Signature Status Badge */}
          {signatures.length > 0 ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Badge
                      variant='default'
                      className='flex items-center gap-1.5 cursor-default'
                    >
                      <CheckCircle className='h-3.5 w-3.5' />
                      Signed by {signatures.length}{' '}
                      {signatures.length === 1 ? 'person' : 'people'}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent side='bottom' className='max-w-xs'>
                  <ul className='space-y-1 text-xs'>
                    {signatures.map(sig => (
                      <li key={sig.id}>
                        <span className='font-medium'>
                          {(sig.signature_metadata?.user_name as string) ||
                            'Unknown User'}
                        </span>
                        <span className='text-muted-foreground ml-1'>
                          {new Date(sig.signed_at).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Badge variant='outline' className='flex items-center gap-1.5'>
              <AlertCircle className='h-3.5 w-3.5' />
              Not Signed
            </Badge>
          )}

          {/* Break Signatures Button (header, only for editable states with signatures) */}
          {canBreakSignatures && !isReadonly && (
            <Button
              onClick={handleBreakSignatures}
              disabled={editingLoading}
              size='sm'
              variant='outline'
              className='flex items-center gap-1.5'
            >
              {editingLoading ? (
                <>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                  Processing...
                </>
              ) : (
                <>
                  <Pencil className='h-4 w-4' />
                  {editButtonLabel}
                </>
              )}
            </Button>
          )}

          {/* Sign Button */}
          {canSign && (
            <Button
              onClick={handleSignAndSubmit}
              disabled={
                signingLoading ||
                currentUserHasSigned ||
                attestationsLoading ||
                !visitAttestation
              }
              size='sm'
              variant={currentUserHasSigned ? 'secondary' : 'default'}
              className='flex items-center gap-1.5'
            >
              {signingLoading ? (
                <>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                  Signing...
                </>
              ) : (
                <>
                  <CheckCircle className='h-4 w-4' />
                  {currentUserHasSigned ? 'Already Signed' : signButtonLabel}
                </>
              )}
            </Button>
          )}

          {/* Sign & Complete Button */}
          {canApprove && (
            <Button
              onClick={handleApprove}
              disabled={
                approvingLoading || attestationsLoading || !visitAttestation
              }
              size='sm'
              variant='default'
              className='flex items-center gap-1.5'
            >
              {approvingLoading ? (
                <>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                  Completing...
                </>
              ) : (
                <>
                  <ShieldCheck className='h-4 w-4' />
                  Sign & Complete
                </>
              )}
            </Button>
          )}

          {/* Task Notes Dialog Trigger */}
          <Button
            variant='outline'
            size='sm'
            onClick={() => {
              setDraftNotes(taskNotes);
              setShowNotesDialog(true);
            }}
            className='flex items-center gap-1.5'
          >
            <StickyNote className='h-4 w-4' />
            Notes
          </Button>

          {/* Print View Button */}
          <Button
            size='sm'
            variant='outline'
            onClick={handleExportPdf}
            disabled={pdfLoading}
            className='flex items-center gap-1.5'
          >
            {pdfLoading ? (
              <>
                <div className='animate-spin rounded-full h-3 w-3 border-b-2 border-current' />
                Opening...
              </>
            ) : (
              <>
                <ExternalLink className='h-4 w-4' />
                Print view
              </>
            )}
          </Button>

          {/* Status Badge */}
          <Badge
            variant={getStatusBadgeVariant(visit.status)}
            className='text-sm'
          >
            {STATUS_DISPLAY_LABELS[visit.status] || visit.status}
          </Badge>
        </div>
      </div>

      {/* Main Content Area */}
      <div className='flex-1 overflow-auto p-5 space-y-4'>
        {pdfError && (
          <div className='text-sm text-destructive bg-destructive/10 p-3 rounded-md'>
            {pdfError}
          </div>
        )}

        {saveError && (
          <div className='text-sm text-destructive bg-destructive/10 p-3 rounded-md'>
            {saveError}
          </div>
        )}

        {/* Signature Invalidation Warning (editable states with active signatures) */}
        {isEditable && signatures.length > 0 && (
          <div className='bg-amber-50 border border-amber-200 rounded-lg p-4'>
            <div className='flex items-start gap-3'>
              <AlertCircle className='h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0' />
              <div className='flex-1'>
                <h4 className='font-semibold text-amber-900 mb-1'>
                  Signature Notice
                </h4>
                <p className='text-sm text-amber-800 mb-3'>
                  This visit has been signed by {signatures.length}{' '}
                  {signatures.length === 1 ? 'person' : 'people'}. Editing the
                  visit data will invalidate all signatures and require
                  re-signing.
                </p>
                <div className='space-y-2'>
                  {signatures.map(sig => (
                    <SignatureCard key={sig.id} signature={sig} tone='amber' />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Visit Content: Readonly or Editable */}
        {isReadonly ? (
          <div className='space-y-4'>
            <div className='rounded-lg border bg-muted/40 p-3'>
              <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'>
                  <p className='text-sm font-medium'>
                    {visit.status === 'completed'
                      ? 'Completed Chart (read-only)'
                      : 'Signed Chart (read-only)'}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {visit.status === 'completed'
                      ? 'This visit has been approved and completed.'
                      : 'This visit is locked. Select Edit to remove signatures and return to the editable form.'}
                  </p>
                </div>
                {canBreakSignatures && (
                  <Button
                    size='default'
                    variant='default'
                    onClick={handleReturnToEditMode}
                    disabled={invalidationLoading}
                    className='flex items-center gap-1.5 shrink-0 font-medium shadow-sm'
                  >
                    <LockOpen className='h-4 w-4' />
                    {editButtonLabel}
                  </Button>
                )}
              </div>
            </div>
            <div className='h-[calc(100vh-14rem)] min-h-[760px]'>
              <ReadonlyChartView visitId={visitId} />
            </div>
          </div>
        ) : (
          <OASISForm
            key={formKey}
            visitId={visitId}
            visitType={visit.visit_type || 'SOC'}
            initialData={visit.visit_data as OASISFormData}
            onSave={handleOASISFormSave}
            hasActiveSignatures={signatures.length > 0}
            showInlineValidation
          />
        )}
        <div className='mt-4'>
          <TaskDocumentsSection taskId={visitId} />
        </div>
      </div>

      {/* Dialogs */}
      <SignatureInvalidationDialog
        open={showConfirmDialog}
        onConfirm={isUnlockAction ? handleUnlockConfirm : handleConfirmSave}
        onCancel={handleCancelSave}
        signatures={signatures}
        isLoading={invalidationLoading}
        actionLabel={isUnlockAction ? editButtonLabel : undefined}
      />

      <AttestationDialog
        open={signAttestationOpen}
        attestation={visitAttestation}
        signerName={user?.name ?? null}
        isLoading={signingLoading}
        actionLabel='Sign & Submit'
        onConfirm={doSignAndSubmit}
        onCancel={() => setSignAttestationOpen(false)}
      />

      <AttestationDialog
        open={approveAttestationOpen}
        attestation={visitAttestation}
        signerName={user?.name ?? null}
        isLoading={approvingLoading}
        actionLabel='Approve & Complete'
        onConfirm={doApprove}
        onCancel={() => setApproveAttestationOpen(false)}
      />

      {/* Task Notes Dialog */}
      <Dialog
        open={showNotesDialog}
        onOpenChange={open => {
          if (!open) setShowNotesDialog(false);
        }}
      >
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Task Notes</DialogTitle>
            <DialogDescription>
              Notes are visible to everyone who handles this chart.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={draftNotes}
            onChange={e => setDraftNotes(e.target.value)}
            placeholder='Add notes about this chart...'
            className='min-h-[150px]'
            autoFocus
          />
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setShowNotesDialog(false)}
              disabled={taskNotesLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!visitId) return;
                if (draftNotes === taskNotes) {
                  setShowNotesDialog(false);
                  return;
                }
                try {
                  setTaskNotesLoading(true);
                  setSaveError(null);
                  await updateVisitNotes({
                    path: { visit_id: visitId },
                    body: { notes: draftNotes || null },
                    client,
                  });
                  setTaskNotes(draftNotes);
                  setShowNotesDialog(false);
                } catch (err) {
                  console.error('Error saving task notes:', err);
                  setSaveError('Failed to save task notes.');
                } finally {
                  setTaskNotesLoading(false);
                }
              }}
              disabled={taskNotesLoading}
            >
              {taskNotesLoading ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                <>
                  <Save className='h-4 w-4' />
                  Save Notes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transition Notes Modal */}
      <Dialog
        open={showTransitionNotesModal}
        onOpenChange={open => {
          if (!open) handleCancelTransition();
        }}
      >
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Task Notes</DialogTitle>
            <DialogDescription>
              Update the task notes before proceeding. These notes are visible
              to everyone who handles this chart.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={transitionNotes}
            onChange={e => setTransitionNotes(e.target.value)}
            placeholder='Add a note about this action...'
            className='min-h-[150px]'
            autoFocus
          />
          <DialogFooter>
            <Button variant='outline' onClick={handleCancelTransition}>
              Cancel
            </Button>
            <Button onClick={handleConfirmTransition}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
